import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core'
import { LogEmail, LogWhatsapp, Merchant } from '@parkupp/core'
import { LogEmailService } from 'src/app/services/log-email.service'
import { LogWhatsappService } from 'src/app/services/log-whatsapp.service'
import { MerchantService } from 'src/app/services/merchant.service'

type AuditChannel = 'whatsapp' | 'email'

interface DateRangeOption {
    label: string
    days: number | null
}

@Component({
    selector: 'app-merchant-profile-audit',
    templateUrl: './merchant-profile-audit.component.html',
    styleUrls: ['./merchant-profile-audit.component.scss'],
})
export class MerchantProfileAuditComponent implements OnInit, OnChanges {
    @Input() merchant: Merchant

    channel: AuditChannel = 'whatsapp'
    loading = false
    error: string | null = null

    logWhatsapps: LogWhatsapp[] = []
    logEmails: LogEmail[] = []

    filteredWhatsapps: LogWhatsapp[] = []
    filteredEmails: LogEmail[] = []

    expandedKey: string | null = null

    readonly dateRangeOptions: DateRangeOption[] = [
        { label: 'Last 7 days', days: 7 },
        { label: 'Last 30 days', days: 30 },
        { label: 'Last 90 days', days: 90 },
        { label: 'All', days: null },
    ]
    selectedDays: number | null = 30

    constructor(
        private merchantService: MerchantService,
        private logWhatsappService: LogWhatsappService,
        private logEmailService: LogEmailService
    ) {}

    ngOnInit(): void {
        if (this.merchant?.$key) {
            this.loadAudit()
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['merchant'] && this.merchant?.$key) {
            this.loadAudit()
        }
    }

    async loadAudit() {
        if (!this.merchant?.$key) return
        this.loading = true
        this.error = null
        try {
            const merchantDocRef = this.merchantService.getDocRef(this.merchant.$key) as any
            const [whatsapps, emails] = await Promise.all([
                this.logWhatsappService.listByMerchant(merchantDocRef, 100).catch((e) => {
                    console.error('[MerchantAudit] listByMerchant (whatsapp) failed:', e)
                    return [] as LogWhatsapp[]
                }),
                this.logEmailService.listByMerchant(merchantDocRef).catch((e) => {
                    console.error('[MerchantAudit] listByMerchant (email) failed:', e)
                    return [] as LogEmail[]
                }),
            ])
            this.logWhatsapps = whatsapps
            this.logEmails = emails
            this.applyFilter()
        } catch (e: any) {
            console.error('[MerchantAudit] loadAudit failed:', e)
            this.error = e?.message || 'Failed to load audit logs'
        } finally {
            this.loading = false
        }
    }

    setChannel(channel: AuditChannel) {
        this.channel = channel
        this.expandedKey = null
    }

    setDays(days: number | null) {
        this.selectedDays = days
        this.applyFilter()
    }

    applyFilter() {
        if (this.selectedDays === null) {
            this.filteredWhatsapps = this.logWhatsapps
            this.filteredEmails = this.logEmails
            return
        }
        const cutoff = new Date()
        cutoff.setDate(cutoff.getDate() - this.selectedDays)

        this.filteredWhatsapps = this.logWhatsapps.filter((log) => {
            const ts: any = log.createdAt
            if (!ts || typeof ts.toDate !== 'function') return true
            return ts.toDate() >= cutoff
        })
        this.filteredEmails = this.logEmails.filter((log) => {
            const ts: any = log.createdAt
            if (!ts || typeof ts.toDate !== 'function') return true
            return ts.toDate() >= cutoff
        })
    }

    toggleExpand(key: string | undefined) {
        if (!key) return
        this.expandedKey = this.expandedKey === key ? null : key
    }

    statusBadgeClass(status: string | undefined): string {
        switch (status) {
            case 'sent':
                return 'status-badge status-badge--sent'
            case 'failed':
                return 'status-badge status-badge--failed'
            case 'pending':
                return 'status-badge status-badge--pending'
            default:
                return 'status-badge'
        }
    }

    refresh() {
        this.loadAudit()
    }
}
